import React, { useState } from 'react';
import { Star, Clock, ChefHat, Calendar, User as UserIcon, Edit3, Send, ShoppingCart, User } from 'lucide-react';
import { NavigationDropdown } from './NavigationDropdown';
export type ViewRecipe = {
  id: string;
  thumbnail: string;
  title: string;
  userRating: number;
  averageRating: number;
  totalRatings: number;
  description: string;
  prepTime: string;
  cookTime: string;
  difficulty: string;
  createdBy: string;
  createdByAvatar: string;
  createdOn: string;
  defaultServings: number;
  tags: string[];
  ingredients: Array<{
    item: string;
    amount: number;
    unit: string;
  }>;
  instructions: string[];
  storage: string;
  reheating: string;
  comments: Array<{
    id: string;
    author: string;
    authorAvatar: string;
    content: string;
    timestamp: string;
  }>;
};
interface RecipeViewOnlyProps {
  recipe: ViewRecipe;
  onBack: () => void;
  onMakeRevision: (recipeId: string) => void;
  currentUserAvatar: string;
  currentUserName: string;
  onAddToCart: (recipeId: string, recipeName: string, thumbnail: string, servings: number, ingredients: any[]) => void;
  onOpenCart: () => void;
  cartItemCount: number;
  onEditProfile: () => void;
  onNavigateHome: () => void;
  onNavigateLanding?: () => void;
  onGenerateRecipe: () => void;
  onImportRecipe: () => void;
  onBrowseRecipes: () => void;
  onManageGuests: () => void;
  navigationContext?: 'home' | 'browse';
  shouldPulseAddToCart?: boolean;
  shouldPulseCart?: boolean;
}
export function RecipeViewOnly({
  recipe,
  onBack,
  onMakeRevision,
  currentUserAvatar,
  currentUserName,
  onAddToCart,
  onOpenCart,
  cartItemCount,
  onEditProfile,
  onNavigateHome,
  onNavigateLanding,
  onGenerateRecipe,
  onImportRecipe,
  onBrowseRecipes,
  onManageGuests,
  navigationContext = 'home',
  shouldPulseAddToCart = false,
  shouldPulseCart = false
}: RecipeViewOnlyProps) {
  const [servings, setServings] = useState(recipe.defaultServings);
  const [userRating, setUserRating] = useState(recipe.userRating);
  const [hoverRating, setHoverRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState(recipe.comments);
  const servingMultiplier = servings / recipe.defaultServings;
  const handleRating = (rating: number) => {
    setUserRating(rating);
  };
  const handleAddComment = () => {
    if (newComment.trim()) {
      const comment = {
        id: Date.now().toString(),
        author: 'You',
        authorAvatar: currentUserAvatar,
        content: newComment,
        timestamp: new Date().toISOString()
      };
      setComments([comment, ...comments]);
      setNewComment('');
    }
  };
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-538">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 sticky top-0 z-10" data-id="element-539">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between" data-id="element-540">
          <NavigationDropdown onNavigateHome={onNavigateHome} onNavigateLanding={onNavigateLanding} onGenerateRecipe={onGenerateRecipe} onImportRecipe={onImportRecipe} onBrowseRecipes={onBrowseRecipes} onManageGuests={onManageGuests} defaultAction={navigationContext} data-id="element-541" />

          <div className="flex items-center space-x-3" data-id="element-542">
            {/* Shopping Cart Icon */}
            <button onClick={onOpenCart} className={`relative flex items-center space-x-2 px-3 py-2 border-2 border-gray-300 hover:border-gray-500 transition-colors bg-white ${shouldPulseCart ? 'animate-pulse-yellow' : ''}`} data-id="element-543">
              <ShoppingCart className="w-5 h-5 text-gray-600" data-id="element-544" />
              {cartItemCount > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-gray-900 text-white text-xs font-mono flex items-center justify-center" data-id="element-545">
                  {cartItemCount}
                </span>}
            </button>

            {/* Profile Button */}
            <button onClick={onEditProfile} className="flex items-center space-x-2 px-3 py-2 border-2 border-gray-300 hover:border-gray-500 transition-colors bg-white" data-id="element-546">
              <div className="w-6 h-6 border border-gray-300 flex items-center justify-center text-sm" data-id="element-547">
                {currentUserAvatar || <User className="w-3 h-3 text-gray-400" data-id="element-548" />}
              </div>
              <span className="font-mono text-xs text-gray-600 hidden sm:inline" data-id="element-549">
                {currentUserName || 'User'}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Recipe Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-6" data-id="element-550">
        {/* Action Buttons - Right aligned WITH PULSING ADD TO CART */}
        <div className="flex flex-wrap gap-3 justify-end" data-id="element-551">
          <button onClick={() => onAddToCart(recipe.id, recipe.title, recipe.thumbnail, recipe.defaultServings, recipe.ingredients)} className={`flex items-center space-x-2 px-4 py-3 border-2 border-gray-300 hover:border-gray-500 transition-colors bg-white ${shouldPulseAddToCart ? 'animate-pulse-yellow' : ''}`} data-id="element-552">
            <ShoppingCart className="w-4 h-4 text-gray-600" data-id="element-553" />
            <span className="font-mono text-xs uppercase tracking-widest" data-id="element-554">
              Add to Cart
            </span>
          </button>

          <button onClick={() => onMakeRevision(recipe.id)} className="flex items-center space-x-2 px-4 py-3 bg-gray-900 text-white hover:bg-gray-800 transition-colors" data-id="element-555">
            <Edit3 className="w-4 h-4" data-id="element-556" />
            <span className="font-mono text-xs uppercase tracking-widest" data-id="element-557">
              Make Revision
            </span>
          </button>
        </div>

        {/* Header Section - RESPONSIVE */}
        <div className="bg-white border-2 border-gray-300 p-4 md:p-6 space-y-4 md:space-y-6" data-id="element-558">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6" data-id="element-559">
            <div className="flex-shrink-0 mx-auto md:mx-0" data-id="element-560">
              <div className="w-32 h-32 border-2 border-gray-300 bg-gray-100 flex items-center justify-center text-6xl" data-id="element-561">
                {recipe.thumbnail}
              </div>
            </div>

            <div className="flex-1 space-y-3" data-id="element-562">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight" data-id="element-563">
                {recipe.title}
              </h1>

              <div className="space-y-2" data-id="element-564">
                <div className="flex flex-wrap items-center gap-2" data-id="element-565">
                  <div className="flex items-center space-x-1" onMouseLeave={() => setHoverRating(0)} data-id="element-566">
                    {[1, 2, 3, 4, 5].map(rating => <button key={rating} onClick={() => handleRating(rating)} onMouseEnter={() => setHoverRating(rating)} className="focus:outline-none" data-id="element-567">
                        <Star className={`w-5 h-5 transition-colors ${rating <= (hoverRating || userRating) ? 'fill-gray-800 text-gray-800' : 'text-gray-300'}`} data-id="element-568" />
                      </button>)}
                  </div>
                  <span className="text-sm font-mono text-gray-500" data-id="element-569">
                    Your rating: {userRating > 0 ? userRating : 'Not rated'}
                  </span>
                </div>
                <p className="text-xs font-mono text-gray-400" data-id="element-570">
                  Average: {recipe.averageRating.toFixed(1)} (
                  {recipe.totalRatings} ratings)
                </p>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-571">
                {recipe.description}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200" data-id="element-572">
            {recipe.tags.map((tag, idx) => <span key={idx} className="inline-flex items-center px-2 py-1 border border-gray-300 bg-white text-xs font-mono text-gray-600" data-id="element-573">
                {tag}
              </span>)}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 p-3 md:p-4 border-2 border-dashed border-gray-200 bg-gray-50" data-id="element-574">
            <div className="space-y-1" data-id="element-575">
              <div className="flex items-center space-x-1 text-gray-400" data-id="element-576">
                <Clock className="w-3 h-3" data-id="element-577" />
                <span className="font-mono text-xs uppercase" data-id="element-578">Prep</span>
              </div>
              <p className="text-sm font-bold text-gray-800" data-id="element-579">
                {recipe.prepTime}
              </p>
            </div>
            <div className="space-y-1" data-id="element-580">
              <div className="flex items-center space-x-1 text-gray-400" data-id="element-581">
                <Clock className="w-3 h-3" data-id="element-582" />
                <span className="font-mono text-xs uppercase" data-id="element-583">Cook</span>
              </div>
              <p className="text-sm font-bold text-gray-800" data-id="element-584">
                {recipe.cookTime}
              </p>
            </div>
            <div className="space-y-1" data-id="element-585">
              <div className="flex items-center space-x-1 text-gray-400" data-id="element-586">
                <ChefHat className="w-3 h-3" data-id="element-587" />
                <span className="font-mono text-xs uppercase" data-id="element-588">Level</span>
              </div>
              <p className="text-sm font-bold text-gray-800" data-id="element-589">
                {recipe.difficulty}
              </p>
            </div>
            <div className="space-y-1" data-id="element-590">
              <div className="flex items-center space-x-1 text-gray-400" data-id="element-591">
                <UserIcon className="w-3 h-3" data-id="element-592" />
                <span className="font-mono text-xs uppercase" data-id="element-593">By</span>
              </div>
              <div className="flex items-center space-x-1" data-id="element-594">
                <span className="text-lg" data-id="element-595">{recipe.createdByAvatar}</span>
                <p className="text-sm font-bold text-gray-800 truncate" data-id="element-596">
                  {recipe.createdBy}
                </p>
              </div>
            </div>
            <div className="space-y-1" data-id="element-597">
              <div className="flex items-center space-x-1 text-gray-400" data-id="element-598">
                <Calendar className="w-3 h-3" data-id="element-599" />
                <span className="font-mono text-xs uppercase" data-id="element-600">Date</span>
              </div>
              <p className="text-sm font-bold text-gray-800" data-id="element-601">
                {recipe.createdOn}
              </p>
            </div>
          </div>
        </div>

        {/* Ingredients with Portion Calculator */}
        <div className="bg-white border-2 border-gray-300 p-4 md:p-6 space-y-4" data-id="element-602">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-603">
            <h2 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-604">
              Ingredients
            </h2>
            <div className="flex items-center space-x-3" data-id="element-605">
              <span className="font-mono text-xs text-gray-500" data-id="element-606">Servings:</span>
              <div className="flex items-center border-2 border-gray-300" data-id="element-607">
                <button onClick={() => setServings(Math.max(1, servings - 1))} className="px-3 py-1 border-r-2 border-gray-300 hover:bg-gray-100 font-mono text-sm" data-id="element-608">
                  −
                </button>
                <span className="px-4 py-1 font-mono text-sm font-bold" data-id="element-609">
                  {servings}
                </span>
                <button onClick={() => setServings(servings + 1)} className="px-3 py-1 border-l-2 border-gray-300 hover:bg-gray-100 font-mono text-sm" data-id="element-610">
                  +
                </button>
              </div>
            </div>
          </div>

          <ul className="space-y-2 border border-gray-200 p-3 md:p-4 bg-white" data-id="element-611">
            {recipe.ingredients.map((ingredient, idx) => <li key={idx} className="flex items-start space-x-3 text-sm" data-id="element-612">
                <span className="font-mono text-gray-300 select-none w-6 flex-shrink-0" data-id="element-613">
                  {(idx + 1).toString().padStart(2, '0')}
                </span>
                <span className="text-gray-700" data-id="element-614">
                  <strong className="font-mono" data-id="element-615">
                    {(ingredient.amount * servingMultiplier).toFixed(1)}{' '}
                    {ingredient.unit}
                  </strong>{' '}
                  {ingredient.item}
                </span>
              </li>)}
          </ul>
        </div>

        {/* Instructions */}
        <div className="bg-white border-2 border-gray-300 p-4 md:p-6 space-y-4" data-id="element-616">
          <h2 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-617">
            Preparation Instructions
          </h2>

          <ol className="space-y-4 border border-gray-200 p-3 md:p-4 bg-white" data-id="element-618">
            {recipe.instructions.map((instruction, idx) => <li key={idx} className="flex gap-3 text-sm" data-id="element-619">
                <span className="font-mono text-gray-400 font-bold flex-shrink-0" data-id="element-620">
                  {(idx + 1).toString().padStart(2, '0')}.
                </span>
                <p className="text-gray-700 leading-relaxed" data-id="element-621">{instruction}</p>
              </li>)}
          </ol>
        </div>

        {/* Storage & Reheating */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-622">
          <div className="bg-white border-2 border-gray-300 p-4 md:p-6 space-y-2" data-id="element-623">
            <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-624">
              Storage Instructions
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-625">
              {recipe.storage}
            </p>
          </div>
          <div className="bg-white border-2 border-gray-300 p-4 md:p-6 space-y-2" data-id="element-626">
            <h3 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-627">
              Reheating Instructions
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-628">
              {recipe.reheating}
            </p>
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-white border-2 border-gray-300 p-4 md:p-6 space-y-6" data-id="element-629">
          <h2 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-630">
            Comments ({comments.length})
          </h2>

          <div className="space-y-3" data-id="element-631">
            <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Share your thoughts about this recipe..." rows={3} className="w-full border-2 border-gray-300 bg-white px-3 md:px-4 py-2 md:py-3 font-mono text-sm focus:border-gray-500 focus:outline-none placeholder:text-gray-300" data-id="element-632" />
            <div className="flex justify-end" data-id="element-633">
              <button onClick={handleAddComment} disabled={!newComment.trim()} className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-634">
                <Send className="w-3 h-3" data-id="element-635" />
                <span className="font-mono text-xs uppercase tracking-widest" data-id="element-636">
                  Post Comment
                </span>
              </button>
            </div>
          </div>

          <div className="space-y-4" data-id="element-637">
            {comments.length === 0 ? <div className="border-2 border-dashed border-gray-200 p-6 md:p-8 text-center" data-id="element-638">
                <p className="font-mono text-sm text-gray-400" data-id="element-639">
                  No comments yet. Be the first to comment!
                </p>
              </div> : comments.map(comment => <div key={comment.id} className="border border-gray-200 p-3 md:p-4 space-y-2" data-id="element-640">
                  <div className="flex items-center space-x-3" data-id="element-641">
                    <div className="w-6 h-6 border border-gray-300 flex items-center justify-center text-sm flex-shrink-0" data-id="element-642">
                      {comment.authorAvatar}
                    </div>
                    <span className="font-mono text-xs font-bold text-gray-700" data-id="element-643">
                      {comment.author}
                    </span>
                    <span className="font-mono text-xs text-gray-400" data-id="element-644">
                      {new Date(comment.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed pl-0 md:pl-9" data-id="element-645">
                    {comment.content}
                  </p>
                </div>)}
          </div>
        </div>
      </div>
    </div>;
}